import React, { JSX } from 'react';
import {
  Card,
  Container,
  Typography,
  Tabs,
  Tab,
  Box,
  Stack,
  Button,
  Grid,
  Chip,
  Skeleton,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Page from 'src/components/Page';
import { useAuth } from 'src/context/auth/AuthContext';
import { financialSimulationService } from 'src/services/financial-simulation.service';
import { StudentChallenges } from './components/StudentChallenges';
import { ScenarioLibrary } from './components/ScenarioLibrary';

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle: string;
  icon: string;
  iconBg: string;
  iconColor: string;
  isLoading?: boolean;
}

function MetricCard({ title, value, subtitle, icon, iconBg, iconColor, isLoading }: MetricCardProps) {
  return (
    <Card
      sx={{
        p: 1.75,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        transition: 'all 0.15s ease-in-out',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
        },
      }}
    >
      <Box>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.6875rem' }}
        >
          {title}
        </Typography>
        {isLoading ? (
          <Skeleton variant="text" width={50} height={28} />
        ) : (
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', my: 0.25, fontSize: '1.35rem', letterSpacing: '-0.02em' }}>
            {value}
          </Typography>
        )}
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
          {subtitle}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: 1.75,
          bgcolor: iconBg,
          color: iconColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon icon={icon} width={20} height={20} />
      </Box>
    </Card>
  );
}

export default function AdminFinancialSimulations(): JSX.Element {
  const navigate = useNavigate();
  const { user, hasPermission, getBaseRoute } = useAuth();
  const [currentTab, setCurrentTab] = React.useState<number>(0);

  const canViewAll = hasPermission('whammies.view_all');
  const canAssign = hasPermission('whammies.assign');
  const canCreate = hasPermission('whammies.create');

  const { data: metrics, isLoading: isLoadingMetrics } = useQuery({
    queryKey: ['whammies-metrics', { teacherUserId: !canViewAll ? (user?.id || user?.email) : undefined }],
    queryFn: () =>
      financialSimulationService.getMetrics({
        teacherUserId: !canViewAll ? (user?.id || user?.email) : undefined,
      }),
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const assignChallengeRoute = getBaseRoute('/assign-new-chellenge');
  const addScenarioRoute = getBaseRoute('/add-scenario');

  return (
    <Page title="Whammies | Gold Minds Portal">
      <Container maxWidth={false} sx={{ px: { xs: 1, sm: 1.5, md: 2 }, pt: 0.5, pb: 4 }}>
        {/* Header Section */}
        <Box sx={{ mb: 2, mt: 0.25 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5}>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.25}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    bgcolor: 'rgba(237, 180, 46, 0.15)',
                    color: '#D97706',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon icon="solar:danger-bold-duotone" width={22} />
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: -0.3, fontSize: '1.35rem' }}>
                    Whammies
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                    Real-world financial challenges, unexpected life events, and classroom economy simulations.
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Stack direction="row" spacing={1}>
              {canCreate && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Icon icon="solar:folder-with-files-bold-duotone" width={17} />}
                  onClick={() => navigate(addScenarioRoute)}
                  sx={{
                    borderRadius: 2,
                    px: 1.75,
                    height: 36,
                    fontWeight: 700,
                    fontSize: '0.8125rem',
                    textTransform: 'none',
                    borderColor: 'divider',
                    color: 'text.primary',
                    bgcolor: 'background.paper',
                    '&:hover': {
                      bgcolor: 'action.hover',
                      borderColor: 'text.secondary',
                    },
                  }}
                >
                  + New Scenario
                </Button>
              )}

              {canAssign && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Icon icon="solar:shield-warning-bold-duotone" width={17} />}
                  onClick={() => navigate(assignChallengeRoute)}
                  sx={{
                    borderRadius: 2,
                    px: 2,
                    height: 36,
                    fontWeight: 700,
                    fontSize: '0.8125rem',
                    textTransform: 'none',
                    bgcolor: 'primary.main',
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                      boxShadow: '0px 2px 8px rgba(46, 125, 50, 0.25)',
                    },
                  }}
                >
                  + Assign Whammy
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>

        {/* Metrics Grid */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Active In Progress"
              value={metrics?.inProgressCount ?? 0}
              subtitle="Challenges actively being resolved"
              icon="solar:hourglass-line-bold-duotone"
              iconBg="rgba(245, 158, 11, 0.12)"
              iconColor="#D97706"
              isLoading={isLoadingMetrics}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Completed Challenges"
              value={metrics?.completedCount ?? 0}
              subtitle="Successfully settled by scholars"
              icon="solar:check-circle-bold-duotone"
              iconBg="rgba(16, 185, 129, 0.12)"
              iconColor="#059669"
              isLoading={isLoadingMetrics}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Overdue Challenges"
              value={metrics?.overdueCount ?? 0}
              subtitle="Requires educator follow-up"
              icon="solar:alarm-bold-duotone"
              iconBg="rgba(239, 68, 68, 0.12)"
              iconColor="#DC2626"
              isLoading={isLoadingMetrics}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Scenario Library"
              value={metrics?.totalScenarios ?? 0}
              subtitle="Pre-built simulation templates"
              icon="solar:library-bold-duotone"
              iconBg="rgba(59, 130, 246, 0.12)"
              iconColor="#2563EB"
              isLoading={isLoadingMetrics}
            />
          </Grid>
        </Grid>

        {/* Tabs Bar */}
        <Card
          sx={{
            borderRadius: 2,
            p: 0.5,
            mb: 2,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 'none',
            bgcolor: 'background.paper',
          }}
        >
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            sx={{
              minHeight: 36,
              '& .MuiTabs-indicator': {
                display: 'none',
              },
              '& .MuiTab-root': {
                minHeight: 36,
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8125rem',
                color: 'text.secondary',
                px: 2,
                py: 0.5,
                mr: 0.75,
                transition: 'all 0.15s ease',
                '&.Mui-selected': {
                  color: 'primary.contrastText',
                  bgcolor: 'primary.main',
                  fontWeight: 700,
                  '& .MuiChip-root': {
                    bgcolor: 'rgba(255, 255, 255, 0.25)',
                    color: '#fff',
                  },
                },
                '&:hover:not(.Mui-selected)': {
                  bgcolor: 'action.hover',
                  color: 'text.primary',
                },
              },
            }}
          >
            <Tab
              label={
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Icon icon="solar:users-group-rounded-bold-duotone" width={16} />
                  <span>Assigned Challenges</span>
                  {metrics?.totalChallenges !== undefined && (
                    <Chip
                      size="small"
                      label={metrics.totalChallenges}
                      sx={{
                        height: 18,
                        fontSize: '0.6875rem',
                        fontWeight: 800,
                        px: 0.25,
                        bgcolor: 'rgba(0, 0, 0, 0.06)',
                      }}
                    />
                  )}
                </Stack>
              }
            />
            <Tab
              label={
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Icon icon="solar:book-bookmark-bold-duotone" width={16} />
                  <span>Scenario Library</span>
                  {metrics?.totalScenarios !== undefined && (
                    <Chip
                      size="small"
                      label={metrics.totalScenarios}
                      sx={{
                        height: 18,
                        fontSize: '0.6875rem',
                        fontWeight: 800,
                        px: 0.25,
                        bgcolor: 'rgba(0, 0, 0, 0.06)',
                      }}
                    />
                  )}
                </Stack>
              }
            />
          </Tabs>
        </Card>

        {/* Tab Panels */}
        {currentTab === 0 && <StudentChallenges />}
        {currentTab === 1 && <ScenarioLibrary />}
      </Container>
    </Page>
  );
}
